import React from 'react';
import { Link } from 'react-router-dom';
import CustomerNavbar from './CustomerNavbar';
import Footer from './Footer';

export type AgreementTocItem = {
  id: string;
  title: string;
};

type Props = {
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  toc?: AgreementTocItem[];
  children?: React.ReactNode;
};

const AgreementPageLayout: React.FC<Props> = ({ title, effectiveDate, lastUpdated, toc, children }) => {
  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <div className="print:hidden">
        <CustomerNavbar showDashboardLink={false} showLogoutButton={false} showAuthButtons={true} />
      </div>

      <div className="flex-1 min-w-0 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-6xl mx-auto">
          <div className="mb-4 print:hidden">
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-semibold text-sm text-[#0B4778] hover:text-[#0a3d66]"
            >
              &lt;- Back
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 md:p-8 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between border-b border-gray-200 pb-5">
                <div className="min-w-0">
                  <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    AACore • Agreement
                  </div>
                  <h1 className="mt-1 text-xl md:text-2xl font-bold text-gray-900">{title}</h1>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-semibold">Effective:</span> {effectiveDate}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="inline-flex items-center gap-1">
                      <span className="font-semibold">Last updated:</span> {lastUpdated}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 print:hidden">
                  <button
                    type="button"
                    className="aacore-btn-primary justify-center"
                    onClick={() => window.print()}
                  >
                    Print
                  </button>
                </div>
              </div>

              {children && (
                <div id="top" className="mt-6 agreement-prose">
                  {children}
                </div>
              )}

              <div className="mt-10 print:hidden">
                <a href="#top" className="text-sm font-semibold text-[#0B4778] hover:text-[#0a3d66]">
                  Back to top
                </a>
              </div>
            </div>

            {toc && toc.length > 0 && (
              <aside className="hidden lg:block print:hidden">
                <div className="sticky top-24 bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                  <div className="text-sm font-bold text-gray-900 mb-3">On this page</div>
                  <nav className="space-y-2">
                    {toc.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="block text-sm text-gray-700 hover:text-[#0B4778] leading-snug"
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
};

export default AgreementPageLayout;


